import React, { useState } from "react";
import {
  Warning,
  Play,
  SignOut,
  Trash,
  User,
  Globe,
} from "@phosphor-icons/react";
import { useLanguage } from "../context/LanguageContext";
import { useData } from "../context/DataContext";
import { useModal } from "../hooks/useModal";
import { isDemoMode, setDemoMode } from "../utils/demoData";

const SettingsSection: React.FC<{
  title: string;
  children: React.ReactNode;
  ariaLabel?: string;
}> = ({ title, children, ariaLabel }) => (
  <div className="card" role="region" aria-label={ariaLabel}>
    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
      {title}
    </h2>
    <div className="mt-4">{children}</div>
  </div>
);

const InfoRow: React.FC<{
  label: string;
  value: string | number;
  ariaLabel?: string;
}> = ({ label, value, ariaLabel }) => (
  <div className="flex justify-between" role="listitem">
    <span className="text-gray-600 dark:text-gray-400">{label}:</span>
    <span
      className="text-gray-900 dark:text-white font-medium"
      aria-label={ariaLabel}>
      {value}
    </span>
  </div>
);

const ClearDataModal: React.FC<{
  show: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  items: string[];
  confirmText: string;
}> = ({
  show,
  onCancel,
  onConfirm,
  title,
  description,
  items,
  confirmText,
}) => {
  const modalRef = useModal({ isOpen: show, onClose: onCancel });

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clear-data-modal-title"
      aria-describedby="clear-data-modal-description">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-3 mb-4">
          <div
            className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center"
            aria-hidden="true">
            <Warning className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3
              id="clear-data-modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="text-gray-400 text-sm">
              This action cannot be undone
            </p>
          </div>
        </div>
        <p id="clear-data-modal-description" className="text-gray-300 mb-6">
          {description}
        </p>
        <ul
          className="text-gray-400 text-sm space-y-2 mb-6"
          aria-label="Data that will be deleted">
          {items.map((item, index) => (
            <li key={index}>• {item}</li>
          ))}
        </ul>
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="btn-secondary flex-1"
            aria-label="Cancel clearing data">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="btn-danger flex-1"
            aria-label={`Confirm ${confirmText.toLowerCase()}`}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const Settings: React.FC = () => {
  const { state, dispatch } = useData();
  const { language, setLanguage, t } = useLanguage();
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [showClearFinancialModal, setShowClearFinancialModal] = useState(false);

  const handleClearAllData = () => {
    // Clear localStorage first
    try {
      localStorage.removeItem("runway-app-state");
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }

    // Then dispatch the action to clear the state
    dispatch({ type: "CLEAR_ALL_DATA" });
    setShowClearAllModal(false);
  };

  const handleClearFinancialData = () => {
    // Clear all financial data but keep the user
    dispatch({ type: "CLEAR_FINANCIAL_DATA" });
    setShowClearFinancialModal(false);
  };

  const handleClearAllDataClick = () => {
    setShowClearAllModal(true);
  };

  const handleClearFinancialDataClick = () => {
    setShowClearFinancialModal(true);
  };

  const handleCancelClearAll = () => {
    setShowClearAllModal(false);
  };

  const handleCancelClearFinancial = () => {
    setShowClearFinancialModal(false);
  };

  const handleExitDemoMode = () => {
    // Clear demo state from localStorage
    try {
      localStorage.removeItem("runway-demo-state");
    } catch (error) {
      console.error("Error clearing demo state:", error);
    }

    setDemoMode(false);
    dispatch({ type: "SET_DEMO_MODE", payload: false });
  };

  const isInDemoMode = isDemoMode();

  return (
    <main role="main" aria-label="Settings page" className="space-y-6">
      <div></div>

      {/* Language Settings */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
        <Globe
          className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
          weight="duotone"
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-blue-900 dark:text-blue-100 text-sm">
            {t("settings.language")}
          </h3>
          <p className="text-xs text-blue-700 dark:text-blue-300">
            Choose your preferred language for the application
          </p>
        </div>
        <div className="flex flex-row gap-2 mt-2 sm:mt-0">
          {[
            { code: "en", name: t("language.english") },
            { code: "es", name: t("language.spanish") },
          ].map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code as any)}
              className={`px-5 py-2 rounded border text-base transition-all duration-200 font-medium flex flex-col items-center justify-center min-w-0 min-h-0 h-11 sm:h-11 ${
                language === lang.code
                  ? "border-blue-500 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300"
                  : "border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 text-gray-700 dark:text-gray-300"
              }`}>
              <span>{lang.name}</span>
              {language === lang.code && (
                <span className="text-xs mt-0.5 opacity-75">Current</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Demo Mode Section */}
      {isInDemoMode && (
        <SettingsSection
          title={t("settings.demoMode")}
          ariaLabel="Demo mode settings">
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-800">
              <Play
                className="w-5 h-5 text-orange-600 dark:text-orange-400"
                weight="duotone"
              />
              <div className="flex-1">
                <h3 className="font-medium text-orange-900 dark:text-orange-100">
                  {t("settings.demoModeActive")}
                </h3>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  {t("settings.demoModeDescription")}
                </p>
              </div>
            </div>
            <button
              onClick={handleExitDemoMode}
              className="btn-secondary flex items-center space-x-2"
              aria-label="Exit demo mode">
              <SignOut className="w-4 h-4" weight="duotone" />
              <span>{t("settings.exitDemoMode")}</span>
            </button>
          </div>
        </SettingsSection>
      )}

      {/* Account Information */}
      <SettingsSection
        title="Account Information"
        ariaLabel="Account information">
        <div className="space-y-3">
          <InfoRow
            label="Name"
            value={state.users[0]?.name || "User"}
            ariaLabel="User name"
          />
          <InfoRow
            label="Email"
            value={state.users[0]?.email || "user@example.com"}
            ariaLabel="User email"
          />
          <InfoRow
            label="Age"
            value={state.users[0]?.age || 30}
            ariaLabel="User age"
          />
          <InfoRow
            label="Risk Tolerance"
            value={state.users[0]?.riskTolerance || "moderate"}
            ariaLabel="Risk tolerance level"
          />
        </div>
      </SettingsSection>

      {/* Data Storage */}
      <SettingsSection
        title="Data Storage"
        ariaLabel="Data storage information">
        <div className="space-y-3">
          <InfoRow
            label="Storage Type"
            value="Local Browser Storage"
            ariaLabel="Data storage type"
          />
          <InfoRow
            label="Privacy"
            value="Local Only"
            ariaLabel="Data privacy level"
          />
          <InfoRow label="Sync" value="None" ariaLabel="Data synchronization" />
          <InfoRow
            label="Backup"
            value="Manual"
            ariaLabel="Data backup method"
          />
        </div>
      </SettingsSection>

      {/* Data Statistics */}
      <SettingsSection title="Data Statistics" ariaLabel="Data statistics">
        <div className="space-y-3">
          <InfoRow
            label="Transactions"
            value={state.transactions.length}
            ariaLabel="Number of transactions"
          />
          <InfoRow
            label="Income Sources"
            value={state.incomeSources.length}
            ariaLabel="Number of income sources"
          />
          <InfoRow
            label="Assets"
            value={state.assets.length}
            ariaLabel="Number of assets"
          />
          <InfoRow
            label="Debts"
            value={state.debts.length}
            ariaLabel="Number of debts"
          />
          <InfoRow
            label="Partners"
            value={state.partners.length}
            ariaLabel="Number of partners"
          />
        </div>
      </SettingsSection>

      {/* Privacy Notice */}
      <div
        className="card border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20"
        role="alert"
        aria-label="Data privacy notice">
        <div className="flex items-start space-x-3">
          <Warning
            className="w-5 h-5 text-orange-500 mt-0.5"
            weight="duotone"
            aria-hidden="true"
          />
          <div>
            <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">
              Data Privacy Notice
            </h3>
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
              All your financial data is stored locally in your browser.
              Clearing your browser data will permanently delete all your
              information. Consider backing up important data regularly.
            </p>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <SettingsSection
        title="Data Management"
        ariaLabel="Data management options">
        <div className="space-y-4">
          {/* Clear Financial Data */}
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center space-x-3 mb-3">
              <Trash
                className="w-5 h-5 text-yellow-600 dark:text-yellow-400"
                weight="duotone"
              />
              <h3 className="font-medium text-yellow-900 dark:text-yellow-100">
                Clear Financial Data
              </h3>
            </div>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-4">
              Remove all financial records but keep your user profile and
              settings.
            </p>
            <button
              onClick={handleClearFinancialDataClick}
              className="btn-secondary"
              aria-label="Clear financial data">
              Clear Financial Data
            </button>
          </div>

          {/* Clear All Data */}
          <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex items-center space-x-3 mb-3">
              <Warning
                className="w-5 h-5 text-red-600 dark:text-red-400"
                weight="duotone"
              />
              <h3 className="font-medium text-red-900 dark:text-red-100">
                Clear All Data
              </h3>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300 mb-4">
              This will permanently delete all your data and reset the
              application completely.
            </p>
            <button
              onClick={handleClearAllDataClick}
              className="btn-danger"
              aria-label="Clear all data">
              Clear All Data
            </button>
          </div>
        </div>
      </SettingsSection>

      {/* Clear All Data Modal */}
      <ClearDataModal
        show={showClearAllModal}
        onCancel={handleCancelClearAll}
        onConfirm={handleClearAllData}
        title="Clear All Data?"
        description="This will permanently delete all your financial data and reset the application, including:"
        items={[
          "All transactions and financial records",
          "Income sources and expense data",
          "Assets and investment information",
          "Debts and loan records",
          "Partner financial data",
          "User profile and preferences",
        ]}
        confirmText="Clear All Data"
      />

      {/* Clear Financial Data Modal */}
      <ClearDataModal
        show={showClearFinancialModal}
        onCancel={handleCancelClearFinancial}
        onConfirm={handleClearFinancialData}
        title="Clear Financial Data?"
        description="This will remove all financial records but keep your user profile and settings:"
        items={[
          "All transactions and financial records",
          "Income sources and expense data",
          "Assets and investment information",
          "Debts and loan records",
          "Partner financial data",
        ]}
        confirmText="Clear Financial Data"
      />
    </main>
  );
};

export default Settings;
